import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaRobot, FaPaperPlane, FaTimes, FaChevronRight } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Dialog } from "@mui/material";
import SpreadsheetAI from "./SpreadsheetAI";
import DocumentAgent from "./DocumentAgent";
import TaxAgent from "./TaxAgent";
import ShippingDocumentAgent from "./ShippingDocumentAgent";
import ComplianceAgent from "./ComplianceAgent";
import LabourLawAgent from "../LabourLawAgent/LabourLawAgent";
import configData from "../config";

// ─── AUTH HEADER ──────────────────────────────────────────────────────────────
const getAuthHeader = () => {
  try {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
};

// ─── AGENT REGISTRY ───────────────────────────────────────────────────────────
// To add a new agent: add one entry here. Nothing else needs to change.
const AGENT_REGISTRY = [
  {
    id:        'business-consultant',
    label:     'Business Consultant',
    subtitle:  'Ask anything about your business',
    icon:      '🤖',
    color:     '#3b82f6',
    type:      'drawer',      // opens in right drawer
    license:   'license_allocated_chatwidget',
  },
  {
    id:        'financial-analyst',
    label:     'Financial Analyst',
    subtitle:  'AI-powered Excel analysis',
    icon:      '📊',
    color:     '#10b981',
    type:      'fullpage',    // opens MUI fullscreen dialog
    license:   'license_allocated_spreadsheet_ai',
    component: SpreadsheetAI,
    fileId:    null,
  },
  {
    id:        'document-agent',
    label:     'Document Agent',
    subtitle:  'Analyse and extract from documents',
    icon:      '📄',
    color:     '#8b5cf6',
    type:      'fullpage',
    license:   'license_allocated_document_agent',
    component: DocumentAgent,
    fileId:    'Paperlink_Documentagent_output.xlsx',
  },
  {
    id:        'tax-agent',
    label:     'Tax Agent',
    subtitle:  'GST and tax filing assistance',
    icon:      '🧾',
    color:     '#f59e0b',
    type:      'fullpage',
    license:   'license_allocated_tax_agent',
    component: TaxAgent,
    fileId:    'TaxAgent_21.xlsx',
  },
  {
    id:        'shipping-agent',
    label:     'Shipping Document Agent',
    subtitle:  'Process shipping documents',
    icon:      '📦',
    color:     '#06b6d4',
    type:      'fullpage',
    license:   null,          // null = always show if hasChatWidget (adjust as needed)
    component: ShippingDocumentAgent,
    fileId:    'ShippingDocumentAgent_21.xlsx',
  },
  {
    id:        'compliance-agent',
    label:     'Compliance Agent',
    subtitle:  'Regulatory compliance checks',
    icon:      '🧩',
    color:     '#ec4899',
    type:      'fullpage',
    license:   null,
    component: ComplianceAgent,
    fileId:    'ComplianceAgent_21.xlsx',
  },
  {
    id:        'labour-law',
    label:     'Labour Law Advisor',
    subtitle:  'Employment law guidance',
    icon:      '⚖️',
    color:     '#ef4444',
    type:      'standalone',   // opens independently, no wrapper header
    license:   'license_allocated_labour_law_advisor',
    component: LabourLawAgent,
    fileId:    'LabourLawAgent_21.xlsx',
  },
];

// ─── SUGGESTED QUESTIONS ──────────────────────────────────────────────────────
const SUGGESTED = [
  "What is our gross margin this quarter?",
  "Which product is most profitable?",
  "Why did costs increase last month?",
  "How is our working capital looking?",
];

// ─── CHAT MESSAGE ─────────────────────────────────────────────────────────────
const ChatMessage = ({ msg }) => {
  const isUser = msg.sender === 'user';
  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 12,
      padding: '0 16px',
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg,#3b82f6,#06b6d4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, flexShrink: 0, marginRight: 8, marginTop: 2,
        }}>🤖</div>
      )}
      <div style={{
        maxWidth: '72%',
        padding: '10px 14px',
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        background: isUser ? '#3b82f6' : '#fff',
        color: isUser ? '#fff' : '#0f172a',
        fontSize: 13,
        lineHeight: 1.6,
        fontFamily: 'DM Sans, sans-serif',
        border: isUser ? 'none' : '1px solid #e5e7eb',
        boxShadow: isUser ? '0 2px 8px rgba(59,130,246,0.25)' : '0 1px 3px rgba(0,0,0,0.06)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        display: 'flex',
        alignItems: msg.loading ? 'center' : 'flex-start',
        gap: msg.loading ? 8 : 0,
      }}>
        {msg.text}
        {msg.loading && (
          <div style={{
            display: 'flex', gap: 4, alignItems: 'center',
          }}>
            {[0,1,2].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#94a3b8',
                animation: `bounce 1.2s ${i * 0.2}s infinite`,
              }}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── AGENT LAUNCHER POPUP ────────────────────────────────────────────────────
const AgentLauncher = ({ agents, onSelect, onClose }) => (
  <div style={{
    position: 'fixed',
    bottom: 90, right: 20,
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e5e7eb',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    padding: '8px 0',
    zIndex: 1001,
    minWidth: 240,
    animation: 'slideUp 0.2s ease',
  }}>
    <div style={{
      padding: '8px 16px 10px',
      borderBottom: '1px solid #f1f5f9',
      fontSize: 11, fontWeight: 700,
      color: '#94a3b8',
      textTransform: 'uppercase',
      letterSpacing: '0.7px',
      fontFamily: 'DM Sans, sans-serif',
    }}>Your Assistants</div>

    {agents.map(agent => (
      <div key={agent.id}
        onClick={() => onSelect(agent)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px', cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${agent.color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, flexShrink: 0,
        }}>{agent.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', fontFamily: 'DM Sans, sans-serif' }}>
            {agent.label}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'DM Sans, sans-serif' }}>
            {agent.subtitle}
          </div>
        </div>
        <FaChevronRight size={10} color="#cbd5e1"/>
      </div>
    ))}
  </div>
);

// ─── BUSINESS CONSULTANT DRAWER ───────────────────────────────────────────────
const BusinessConsultantDrawer = ({ open, onClose, orgId, userId, chatLimit }) => {
  const [prompt,   setPrompt]   = useState('');
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (text) => {
    const currentPrompt = text || prompt;
    if (!currentPrompt.trim() || loading) return;
    if (messages.length >= chatLimit) return;

    setMessages(p => [...p, { text: currentPrompt, sender: 'user' }]);
    setPrompt('');
    setLoading(true);

    const steps = [
      "Retrieving data from database...",
      "Analysing with financial model...",
      "Generating response...",
    ];
    let step = 0;
    setMessages(p => [...p, { text: steps[0], sender: 'bot', loading: true }]);

    const interval = setInterval(() => {
      step++;
      if (step < steps.length) {
        setMessages(p => [...p.filter(m => !m.loading), { text: steps[step], sender: 'bot', loading: true }]);
      }
    }, 1500);

    try {
      const res = await fetch(`${configData.API_SERVER}user/get_chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ prompt: currentPrompt, orgId, userId }),
      });
      const data = await res.json();
      clearInterval(interval);
      setMessages(p => [...p.filter(m => !m.loading), { text: (data?.response || '').slice(0, 20000), sender: 'bot' }]);
    } catch {
      clearInterval(interval);
      setMessages(p => [...p.filter(m => !m.loading), { text: '❌ Could not connect. Please try again.', sender: 'bot' }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const isEmpty = messages.length === 0;

  // Drawer width — responsive
  const drawerWidth = window.innerWidth <= 768 ? '100vw' : '45vw';

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15,23,42,0.3)',
          zIndex: 1100,
          animation: 'fadeIn 0.2s ease',
        }}/>
      )}

      {/* Drawer */}
      <div style={{
        position: 'fixed',
        top: 0, right: 0, bottom: 0,
        width: drawerWidth,
        background: '#f8fafc',
        zIndex: 1101,
        display: 'flex',
        flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
      }}>

        {/* Header */}
        <div style={{
          background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          padding: '14px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
          flexShrink: 0,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg,#3b82f6,#06b6d4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>🤖</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 15, color: '#0f172a' }}>
              Business Consultant
            </div>
            <div style={{ fontSize: 11, color: '#10b981', fontFamily: 'DM Sans, sans-serif', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'pulse 1.5s infinite' }}/>
              Online · Powered by IMAGE-I
            </div>
          </div>
          <button onClick={onClose} style={{
            background: '#f1f5f9', border: 'none', borderRadius: 8,
            width: 32, height: 32, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#64748b', transition: 'background 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
            onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
          >
            <FaTimes size={13}/>
          </button>
        </div>

        {/* Messages area */}
        <div style={{
          flex: 1, overflowY: 'auto', paddingTop: 16, paddingBottom: 8,
        }}>

          {/* Welcome state */}
          {isEmpty && (
            <div style={{ padding: '24px 20px' }}>
              <div style={{
                background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb',
                padding: '20px', marginBottom: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>👋</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: '#0f172a', marginBottom: 6 }}>
                  Hello! I'm your Business Consultant.
                </div>
                <div style={{ fontSize: 13, color: '#64748b', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>
                  Ask me anything about your business data — sales, margins, costs, inventory or any financial metric.
                </div>
              </div>

              {/* Suggested questions */}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.7px', fontFamily: 'DM Sans, sans-serif', marginBottom: 10 }}>
                Try asking
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SUGGESTED.map((q, i) => (
                  <button key={i} onClick={() => handleSend(q)} style={{
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 10, padding: '10px 14px',
                    textAlign: 'left', cursor: 'pointer',
                    fontSize: 12, color: '#3b82f6',
                    fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                    transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  >
                    {q}
                    <FaChevronRight size={9} style={{ flexShrink: 0, marginLeft: 8 }}/>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat messages */}
          {messages.map((msg, i) => <ChatMessage key={i} msg={msg}/>)}
          <div ref={messagesEndRef}/>
        </div>

        {/* Input */}
        <div style={{
          background: '#fff',
          borderTop: '1px solid #e5e7eb',
          padding: '14px 16px',
          flexShrink: 0,
        }}>
          <div style={{
            display: 'flex', alignItems: 'flex-end', gap: 10,
            background: '#f8fafc', borderRadius: 12,
            border: '1.5px solid #e2e8f0',
            padding: '8px 8px 8px 14px',
            transition: 'border-color 0.2s',
          }}
            onFocusCapture={e => e.currentTarget.style.borderColor = '#3b82f6'}
            onBlurCapture={e => e.currentTarget.style.borderColor = '#e2e8f0'}
          >
            <textarea
              rows={1}
              value={prompt}
              onChange={e => {
                setPrompt(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={handleKey}
              placeholder="Ask about your business..."
              style={{
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', resize: 'none',
                fontSize: 13, color: '#0f172a',
                fontFamily: 'DM Sans, sans-serif',
                lineHeight: 1.5, minHeight: 22,
                padding: 0,
              }}
            />
            <button onClick={() => handleSend()} disabled={!prompt.trim() || loading} style={{
              width: 34, height: 34, borderRadius: 8, border: 'none',
              background: prompt.trim() && !loading ? '#3b82f6' : '#e2e8f0',
              color: prompt.trim() && !loading ? '#fff' : '#94a3b8',
              cursor: prompt.trim() && !loading ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s', flexShrink: 0,
            }}>
              <FaPaperPlane size={12}/>
            </button>
          </div>
          <div style={{ fontSize: 10, color: '#cbd5e1', textAlign: 'center', marginTop: 8, fontFamily: 'DM Sans, sans-serif' }}>
            Powered by IMAGE-I · Data from your Power BI
          </div>
        </div>
      </div>
    </>
  );
};

// ─── FULLPAGE AGENT WRAPPER ───────────────────────────────────────────────────
const FullPageAgent = ({ agent, onClose }) => {
  if (!agent) return null;
  const AgentComponent = agent.component;

  // standalone = agent has its own full app shell, render directly without wrapper
  if (agent.type === 'standalone') {
    return (
      <Dialog fullScreen open={!!agent} onClose={onClose}>
        <AgentComponent onClose={onClose}/>
      </Dialog>
    );
  }

  // fullpage = wrap with IMAGE-I branded header
  return (
    <Dialog fullScreen open={!!agent} onClose={onClose}>
      <div style={{ display:'flex', flexDirection:'column', height:'100vh', background:'#f8fafc' }}>

        {/* IMAGE-I branded header */}
        <div style={{
          background: '#fff',
          borderBottom: `3px solid ${agent.color}`,
          padding: '0 20px',
          height: 56,
          display: 'flex', alignItems: 'center', gap: 12,
          flexShrink: 0,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {/* Brand mark */}
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'linear-gradient(135deg,#3b82f6,#06b6d4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Syne, sans-serif', fontWeight: 800,
            fontSize: 12, color: '#fff', flexShrink: 0,
          }}>I</div>

          {/* Divider */}
          <div style={{ width: 1, height: 20, background: '#e2e8f0', flexShrink: 0 }}/>

          {/* Agent icon + name */}
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: `${agent.color}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, flexShrink: 0,
          }}>{agent.icon}</div>

          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'Syne, sans-serif', fontWeight: 800,
              fontSize: 15, color: '#0f172a', lineHeight: 1.1,
            }}>{agent.label}</div>
            <div style={{
              fontSize: 10, color: '#94a3b8',
              fontFamily: 'DM Sans, sans-serif',
            }}>IMAGE-I · AI Agent</div>
          </div>

          {/* Back button */}
          <button onClick={onClose} style={{
            background: '#f1f5f9', border: 'none', borderRadius: 8,
            padding: '7px 14px', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, color: '#64748b',
            fontFamily: 'DM Sans, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'background 0.15s', flexShrink: 0,
          }}
            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
            onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
          >
            ← Back
          </button>
        </div>

        {/* Agent content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <AgentComponent onClose={onClose}/>
        </div>

      </div>
    </Dialog>
  );
};

// ─── MAIN CHATWIDGET ──────────────────────────────────────────────────────────
const ChatWidget = () => {
  const orgMember     = useSelector(s => s.account?.org_member);
  const hasChatWidget = orgMember?.license_allocated_chatwidget === true;
  const orgId         = useSelector(s => s?.account?.org?.id);
  const userId        = useSelector(s => s?.account?.org_member?.id);

  const [launcherOpen,   setLauncherOpen]   = useState(false);
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [activeFullPage, setActiveFullPage] = useState(null);
  const [clearingAgent,  setClearingAgent]  = useState(null);

  const chatLimit = [45, 46].includes(orgId) ? Infinity : 10000;

  // ── Must be before early return — hooks cannot be conditional ──
  const handleFullPageClose = useCallback(async () => {
    const agent = activeFullPage;
    if (agent?.fileId) {
      try {
        await fetch(`${configData.API_SERVER}user/clear_excel_data/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify({ file_id: agent.fileId }),
        });
      } catch (e) { console.error('Failed to clear Excel:', e); }
    }
    setActiveFullPage(null);
  }, [activeFullPage]);

  if (!hasChatWidget) return null;

  // Filter agents by license
  const allowedAgents = AGENT_REGISTRY.filter(agent => {
    if (!agent.license) return true;
    return orgMember?.[agent.license] === true;
  });

  const handleAgentSelect = (agent) => {
    setLauncherOpen(false);
    if (agent.type === 'drawer') {
      setDrawerOpen(true);
    } else {
      // both 'fullpage' and 'standalone' open via activeFullPage
      // FullPageAgent handles the difference internally
      setActiveFullPage(agent);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes slideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes bounce  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
      `}</style>

      {/* ── FAB BUTTON ── */}
      <div style={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8,
      }}>

        {/* Agent launcher popup */}
        {launcherOpen && (
          <AgentLauncher
            agents={allowedAgents}
            onSelect={handleAgentSelect}
            onClose={() => setLauncherOpen(false)}
          />
        )}

        {/* FAB */}
        <button
          onClick={() => setLauncherOpen(p => !p)}
          style={{
            width: 56, height: 56, borderRadius: 16, border: 'none',
            background: launcherOpen
              ? '#0f172a'
              : 'linear-gradient(135deg,#3b82f6,#06b6d4)',
            color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(59,130,246,0.4)',
            transition: 'all 0.2s',
            fontSize: 22,
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          title="Open Assistants"
        >
          {launcherOpen ? <FaTimes size={18}/> : <FaRobot size={22}/>}
        </button>

        {/* Badge showing agent count */}
        {!launcherOpen && allowedAgents.length > 1 && (
          <div style={{
            position: 'absolute', top: -6, right: -6,
            width: 18, height: 18, borderRadius: '50%',
            background: '#ef4444', color: '#fff',
            fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'DM Sans, sans-serif',
            border: '2px solid #fff',
          }}>{allowedAgents.length}</div>
        )}
      </div>

      {/* ── BUSINESS CONSULTANT DRAWER ── */}
      <BusinessConsultantDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        orgId={orgId}
        userId={userId}
        chatLimit={chatLimit}
      />

      {/* ── FULLPAGE AGENTS ── */}
      <FullPageAgent
        agent={activeFullPage}
        onClose={handleFullPageClose}
      />

      {/* Close launcher on outside click */}
      {launcherOpen && (
        <div
          onClick={() => setLauncherOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 999 }}
        />
      )}
    </>
  );
};

export default ChatWidget;
